import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TimelineComponent } from '../timeline/timeline.component';
import { DragAndDropTableComponent } from '../drag-and-drop-table/drag-and-drop-table.component';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { FormsModule } from '@angular/forms';
import { BlendersService } from '../../core/services/blenders.service';
import { PackagingMachinesService } from '../../core/services/packaging-machines.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { OrderType, ValueStream } from '../../core/enums/general.enum';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CalendarDay } from '../../core/interfaces/calendar.interface';
import { CameraHeatmap, CameraHeatmapSegment } from '../../core/interfaces/camera-heatmap.interface';
import { buildCameraHeatmapSegments } from '../../core/utils/camera-heatmap-segments.util';

@Component({
  selector: 'app-apo-fine-tunning',
  standalone: true,
  imports: [CommonModule, TimelineComponent, DragAndDropTableComponent, DatepickerComponent, FormsModule, MultiSelectModule, InputSwitchModule],
  templateUrl: './apo-fine-tunning.component.html',
  styleUrl: './apo-fine-tunning.component.scss'
})
export class ApoFineTunningComponent {

  @Input() tasks: any;
  @Input() calendar: any;
  @Input() cameraHeatmap: CameraHeatmap | null = null;
  @Input() selectedVS: ValueStream = ValueStream.VS1;
  @Output() tasksChange: EventEmitter<any> = new EventEmitter<any>();

  isTableView = false;

  allBlenders: any[] = [];
  allPackagingMachines: any[] = [];
  blenders: any[] = [];
  packagingMachines: any[] = [];

  selectedBlenders = [];
  selectedPackagingMachines = [];

  date: Date = new Date();

  blenderTasks: any = [];
  packagingTasks: any = [];
  tableTasks: any = [];

  filteredBlenderTasks: any = [];
  filteredPackagingTasks: any = [];
  filteredTasks: any = [];

  cameraHeatmapSegments: CameraHeatmapSegment[] = [];

  constructor(
    private blendersService: BlendersService,
    private packagingMachinesService: PackagingMachinesService
  ) { }

  private createDateWithUTCHours(source: Date, hours: number, minutes: number, seconds: number): Date {
    return new Date(Date.UTC(
      source.getUTCFullYear(),
      source.getUTCMonth(),
      source.getUTCDate(),
      hours,
      minutes,
      seconds
    ));
  }

  private getCalendarDay(date: Date): CalendarDay | undefined {
    if (!this.calendar) return undefined;
    return this.calendar.find((day: CalendarDay) => {
      const calendarDate = new Date(day.date);
      return calendarDate.getUTCDate() === date.getUTCDate() && 
             calendarDate.getUTCMonth() === date.getUTCMonth() && 
             calendarDate.getUTCFullYear() === date.getUTCFullYear();
    });
  }

  private isWorkingDay(date: Date): boolean {
    const calendarDay = this.getCalendarDay(date);
    return calendarDay ? calendarDay.type === 'laborable' : true;
  }

  private getEndHour(date: Date): number {
    const calendarDay = this.getCalendarDay(date);
    if (calendarDay && calendarDay.end_hour) {
      const [hours, minutes] = calendarDay.end_hour.split(':').map(Number);
      return hours + minutes / 60;
    }
    return 22; // Default fallback
  }

  private getStartHour(date: Date): number {
    const calendarDay = this.getCalendarDay(date);
    if (calendarDay && calendarDay.start_hour) {
      const [hours, minutes] = calendarDay.start_hour.split(':').map(Number);
      return hours + minutes / 60;
    }
    // Default fallback when no calendar data
    return 6;
  }

  private getNextWorkingDay(date: Date): Date {
    let nextDay = new Date(date);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    let maxIterations = 365;
    while (!this.isWorkingDay(nextDay) && maxIterations > 0) {
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      maxIterations--;
    }
    return nextDay;
  }

  ngOnInit() {
    this.blendersService.getBlenders().subscribe((res: any) => {
      this.allBlenders = res.sort((a: any, b: any) => a.id - b.id);
      this.filterByVS();
    });

    this.packagingMachinesService.getPackagingMachines().subscribe((res: any) => {
      this.allPackagingMachines = res.sort((a: any, b: any) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
      this.filterByVS();
    });
  }

  private filterByVS() {
    this.blenders = this.allBlenders.filter((b: any) => b.value_stream === this.selectedVS);
    this.packagingMachines = this.allPackagingMachines.filter((m: any) => m.value_stream === this.selectedVS);
    this.selectedBlenders = [];
    this.selectedPackagingMachines = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedVS'] && !changes['selectedVS'].firstChange) {
      this.filterByVS();
    }
    this.blenderTasks = [];
    this.packagingTasks = [];
    if (this.tasks) {
      this.tasks.forEach((task: any) => {   
        if (task.type === OrderType.Order) {
          const startDate = new Date(task.fecha_inicio_blender);
          const endDate = new Date(task.fecha_fin_blender);
          
          // Skip if start date is not a working day
          if (!this.isWorkingDay(startDate)) {
            return;
          }

          if (startDate.getUTCDate() === endDate.getUTCDate()) {
            task.hora_inicio_blender = startDate.getUTCHours() + startDate.getUTCMinutes() / 60 + startDate.getUTCSeconds() / 3600;
            let newTask = { ...task,
              nuevo_production_time: task.production_time,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
            };
            this.blenderTasks.push(newTask);
          }
          else {
            const startDateTime = new Date(task.fecha_inicio_blender);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = endHour - currentStartHour;
            
            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              fecha_inicio_blender: task.fecha_inicio_blender,
              fecha_fin_blender: endOfDayDate.toISOString(),
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              hora_inicio_blender: currentStartHour,
              nuevo_production_time: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              fecha_inicio_blender: nextDayStartDate.toISOString(),
              fecha_fin_blender: task.fecha_fin_blender,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              hora_inicio_blender: nextStartHour,
              nuevo_production_time: task.production_time - firstFabTime,
            };
            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }

          const startDateQuality = new Date(task.fecha_inicio_calidad);
          const endDateQuality = new Date(task.fecha_fin_calidad);
          
          // Skip if start date is not a working day
          if (!this.isWorkingDay(startDateQuality)) {
            return;
          }

          if (startDateQuality.getUTCDate() === endDateQuality.getUTCDate()) {
            task.hora_inicio_calidad = startDateQuality.getUTCHours() + startDateQuality.getUTCMinutes() / 60 + startDateQuality.getUTCSeconds() / 3600;
            let newTask = { ...task,
              type: OrderType.Quality,
              nuevo_quality_time: task.quality_time,
            };
            this.blenderTasks.push(newTask);
          }
          else {
            const startDateTime = new Date(task.fecha_inicio_calidad);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = endHour - currentStartHour;
            
            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              type: OrderType.Quality,
              nueva_fecha_inicio_calidad: task.fecha_inicio_calidad,
              nueva_fecha_fin_calidad: endOfDayDate.toISOString(),
              hora_inicio_calidad: currentStartHour,
              nuevo_quality_time: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              type: OrderType.Quality,
              nueva_fecha_inicio_calidad: nextDayStartDate.toISOString(),
              nueva_fecha_fin_calidad: task.fecha_fin_calidad,
              hora_inicio_calidad: nextStartHour,
              nuevo_quality_time: task.quality_time - firstFabTime,
            };
            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }

          task.envasadora_orders.forEach((order: any) => {
            const startDatePackaging = new Date(order.fecha_inicio_envasadora);
            const endDatePackaging = new Date(order.fecha_fin_envasadora);
            
            // Skip if start date is not a working day
            if (!this.isWorkingDay(startDatePackaging)) {
              return;
            }

            if (startDatePackaging.getUTCDate() === endDatePackaging.getUTCDate()) {
              order.hora_inicio_envasadora = startDatePackaging.getUTCHours() + startDatePackaging.getUTCMinutes() / 60 + startDatePackaging.getUTCSeconds() / 3600;
              let newTask = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nuevo_packaging_time: order.packaging_time,
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
              }
              this.packagingTasks.push(newTask);
  
            } else {
              const startDateTime = new Date(order.fecha_inicio_envasadora);
              const endHour = this.getEndHour(startDateTime);
              const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
              const firstEnvTime = endHour - currentStartHour;
              
              const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

              let newTask1 = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nueva_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                nueva_fecha_fin_envasadora: endOfDayDate.toISOString(),
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
                hora_inicio_envasadora: currentStartHour,
                nuevo_packaging_time: firstEnvTime,
              };
  
              const nextWorkingDay = this.getNextWorkingDay(startDateTime);
              const nextStartHour = this.getStartHour(nextWorkingDay);
              const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

              let newTask2 = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nueva_fecha_inicio_envasadora: nextDayStartDate.toISOString(),
                nueva_fecha_fin_envasadora: order.fecha_fin_envasadora,
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
                hora_inicio_envasadora: nextStartHour,
                nuevo_packaging_time: order.packaging_time - firstEnvTime,
              };
  
              this.packagingTasks.push(newTask1);
              if (this.isWorkingDay(nextWorkingDay)) {
                this.packagingTasks.push(newTask2);
              }
            }
          });
          
        } else if (task.type === OrderType.Cleaning) {
          const startDateCleaning = new Date(task.fecha_inicio_blender);
          const endDateCleaning = new Date(task.fecha_fin_blender);
          
          // Skip if start date is not a working day
          if (!this.isWorkingDay(startDateCleaning)) {
            return;
          }

          if (startDateCleaning.getUTCDate() === endDateCleaning.getUTCDate()) {
            task.hora_inicio = startDateCleaning.getUTCHours() + startDateCleaning.getUTCMinutes() / 60 + startDateCleaning.getUTCSeconds() / 3600;
            this.blenderTasks.push({ ...task,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
            });
          } else {
            const startDateTime = new Date(task.fecha_inicio_blender);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = (endHour - currentStartHour) * 60;

            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              fecha_inicio_blender: task.fecha_inicio_blender,
              fecha_fin_blender: endOfDayDate.toISOString(),
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              duration: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              fecha_inicio_blender: nextDayStartDate.toISOString(),
              fecha_fin_blender: task.fecha_fin_blender,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              duration: task.duration - firstFabTime,
            };

            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }
        }
      });
    }
    this.getFineTunningData(this.date);
  }
  onDateChange(date: Date) {
    this.date = date;
    this.getFineTunningData(this.date);
  }

  onDateIncrement(increment: number) {
    const newDate = new Date(this.date);
    newDate.setDate(newDate.getDate() + increment);
    this.date = newDate;
    this.getFineTunningData(this.date);
  }

  getFineTunningData(date: Date) {
    if (this.tasks) {
      if (!this.isWorkingDay(date)) {
        this.filteredBlenderTasks = [];
        this.filteredPackagingTasks = [];
        this.tableTasks = this.tasks.filter((task: any) => {
          return task.type === OrderType.Order && !task.is_planned;
        });
      } else {
        this.filteredBlenderTasks = this.blenderTasks.filter((task: any) => {
          if (task.type === 'calidad') {
            const taskDate = new Date(task.nueva_fecha_inicio_calidad ? task.nueva_fecha_inicio_calidad : task.fecha_inicio_calidad);
            return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
          }
          const taskDate = new Date(task.fecha_inicio_blender ? task.fecha_inicio_blender : task.fecha_inicio);
          return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
        });
        this.filteredPackagingTasks = this.packagingTasks.filter((task: any) => {
          const taskDate = new Date(task.nueva_fecha_inicio_envasadora ? task.nueva_fecha_inicio_envasadora : task.fecha_inicio_envasadora);
          return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
        });
        this.tableTasks = this.tasks.filter((task: any) => {
          return task.type === OrderType.Order && !task.is_planned;
        });
      }
    }
    this.cameraHeatmapSegments = buildCameraHeatmapSegments(
      date,
      this.cameraHeatmap,
      (d) => this.isWorkingDay(d)
    );
  }

  onTasksOrderChange(tasks: any) {
    this.tasksChange.emit(tasks);
  }
}
